import { ASM_DIALOGS_MAP, DialogService } from '@/core/services/dialog'
import { BaseModule } from '@core/base/module/base-module'
import { ROUTES } from '@core/config/routing'
import { AccountModule } from '@core/modules/account/account.module'
import { TAccountType } from '@core/modules/account/account'
import { UserModule } from '@core/modules/user/user.module'
import { ProductivityIntegrationsModule } from '@core/modules/productivity-integrations/productivity-integrations.module'
import { ScansService } from '@core/services/api-gateway/scans-service/scans.service'
import { AuthService } from '@core/services/auth'
import { getGoogleIntegrationScope } from '@core/services/api-gateway/integrations-service/integrations/google/googleIntegrationScope'
import { Analytics } from '@core/services/mixpanel'
import { IntercomService } from '@core/services/intercom/intercom.service'
import { DomainsModule } from '../domains/domains.module'
import { UserInputsModule } from '../user-inputs'
import { clearMdrLocalStorage } from '@core/modules/mdr/utils'
import { IntegrationsService } from '@/core/services/api-gateway/integrations-service/integrations.service'

const state = {
    initialized: false,
    loading: false,
    error: false,
    isAuthenticated: false,
    scanInProgress: false,
    features: {},
    forceReload: false,
    shouldEnforceMfa: false,
    allPoliciesExpired: false,
} satisfies IAppState

class App extends BaseModule<IAppState> {
    constructor(state: IAppState) {
        super(state, 'app.module')

        this.$eventBus.on('scan_in_progress', async () => {
            await this.onScanInProgress()
        })

        this.$eventBus.on('scan_finished', async () => {
            this.log('scan_finished')
            await this.onScanFinished()
        })
    }

    private async onBeforeInitialize() {
        const searchParams = await this.getURLSearchParams()
        await this.handleURLSearchParams(searchParams as TURLSearchParams)
    }

    private async backToLogin() {
        clearMdrLocalStorage()
        await AuthService.login()
    }

    public async initialize() {
        const store = this.store

        if (!store.get().initialized) {
            // app not initialized yet, no user data, no account data
            store.setKey('loading', true)

            // before initialize
            await this.onBeforeInitialize()

            // authorize user/login TODO : split into separate functions
            const result = await AccountModule.authorizeUser()

            // if user is not authorized, set error state and return
            if (!result) {
                await this.backToLogin()
                return
            }

            if (result.onlyExpiredAccounts) {
                AccountModule.store.setKey(
                    'expiredAccountsNames',
                    result.expiredAccountsNames ?? []
                )
                store.setKey('allPoliciesExpired', true)
                store.setKey('initialized', true)
                store.setKey('loading', false)
                IntercomService.initialize(result.user, false, false)
                return
            }

            if (!result.account || !result.user) {
                await this.backToLogin()
                return
            }

            await Promise.all([
                this.loadFeatureFlags(result.user, result.account),
                await AccountModule.setAccount(result.account),
                AccountModule.getAccountInfo(),
            ])

            const currentAccountId =
                ((await AccountModule.storage.getItem(
                    'currentAccount'
                )) as string) ?? result.account

            // emit event initialized
            this.$eventBus.emit('app_initialized', {
                user: result.user,
                account: result.account,
                accounts: result.accounts,
                expiredAccountsNames: result.expiredAccountsNames ?? [],
                featureFlags: this.features,
                locationPathname: location.pathname,
            })

            const shouldEnforceMfa = !!(
                result.accounts[currentAccountId].enforce_mfa &&
                !result.user.mfa_active
            )

            if (shouldEnforceMfa) {
                this.handleMfaEnforcement()
            }
            const isPolicyAccount =
                AccountModule.store.get().accountType === 'POLICY'

            if (isPolicyAccount) {
                await this.initializeFullAccount()
            }

            Analytics.event('SEM::user_login_success')
            Analytics.event('SEM::set_account', { account: result.account })

            store.setKey('shouldEnforceMfa', shouldEnforceMfa)
            store.setKey('initialized', true)
            store.setKey('isAuthenticated', true)
        }
    }

    private async initializeFullAccount() {
        UserInputsModule.pollProcesses()

        UserModule.onLoad()
        AccountModule.getScansInfo()
        ScansService.getScans(1000).catch(console.error)

        await ProductivityIntegrationsModule.getWizerIntegration()
    }

    private async getURLSearchParams() {
        if (location.search) {
            // transform query string to object
            const obj: { [key: string]: string } = Object.fromEntries(
                new URLSearchParams(location.search)
            )

            // save query string to local storage
            this.storage.saveItem('qs', obj).catch(console.error)

            Analytics.event('SEM::app_handleURLSearchParams', obj)

            // remove query string from url
            history.replaceState({}, '', location.pathname)

            // return search params as object
            return obj
        }

        return (await this.storage.getItem('qs')) ?? {}
    }

    private async handleURLSearchParams(searchParams: TURLSearchParams) {
        if (!searchParams || !Object.keys(searchParams).length) {
            return
        }
        // handle account activation
        'aid' in searchParams &&
            (await AccountModule.activateAccountFromEmail(searchParams))

        // Microsoft Integration Response
        if ('admin_consent' in searchParams) {
            let localStorageDataMs = JSON.parse(
                localStorage.getItem('/msIntegrationData') ?? '{}'
            )
            if (!('error' in searchParams)) {
                localStorageDataMs = {
                    msTenantId:
                        'tenant' in searchParams ? searchParams.tenant : '',
                    msProviderId: searchParams.state ?? '',
                }
            } else {
                localStorageDataMs = {
                    msTenantId: '',
                    msProviderId: searchParams.state ?? '',
                }
            }
            this.storage.saveItem('msIntegrationData', localStorageDataMs)
            window.close()
        }

        // Google Integration Response
        if ('code' in searchParams && 'scope' in searchParams) {
            let localStorageDataGoogle = JSON.parse(
                localStorage.getItem('/googleIntegrationData') ?? '{}'
            )

            const returnedScope = (searchParams.scope as string)
                ?.split(' ')
                .sort()
            const requestedScope = getGoogleIntegrationScope().split(' ').sort()
            const returnContainsAllRequested = requestedScope.every((scope) =>
                returnedScope.includes(scope)
            )
            localStorageDataGoogle = {
                GoogleTenantId: searchParams.state ?? '',
                GoogleErr: !returnContainsAllRequested || '',
                GoogleCode: returnContainsAllRequested ? searchParams.code : '',
            }
            this.storage.saveItem(
                'googleIntegrationData',
                localStorageDataGoogle
            )
            window.close()
        }

        // Google Integration Error or Cancel
        if ('error' in searchParams && 'state' in searchParams) {
            await this.storage.saveItem(
                'IntegrationErr',
                searchParams.error_description ?? searchParams.error
            )
            window.close()
        }
    }

    private async loadFeatureFlags(user: TCurrentUser, accountId: string) {
        const { FFManager } = await import('@core/services/feature-flags')
        this.features = await FFManager.initialize(user, accountId)
        this.store.setKey('features', this.features)
        Analytics.event('SEM::app_features_loaded')
    }

    public async onUserEnterRoute() {
        if (
            !this.store.get().initialized ||
            !this.store.get().isAuthenticated
        ) {
            await this.initialize().then(
                async () => await this.handleRouteDataLoad()
            )
            return
        }

        await this.handleRouteDataLoad()
    }

    private readonly handleInitByAccountType: Record<
        TAccountType,
        (force: boolean) => Promise<void>
    > = {
        MDR: async () => {
            this.$eventBus.emit('redirect', ROUTES.mdr.path)
        },
        POLICY: async (force: boolean) => {
            const { SummaryModule } =
                await import('@core/modules/summary/summary.module')
            await SummaryModule.loadSummary(force)
        },
    }

    private async handleRouteDataLoad(force = false) {
        if (!this.store.get().isAuthenticated) {
            return
        }

        Analytics.event('SEM::app_handleRouteDataLoad_start', {
            path: location.pathname,
        })

        switch (location.pathname) {
            case ROUTES.summary.path:
                const { accountType } = AccountModule.store.get()
                await this.handleInitByAccountType[accountType as TAccountType](
                    force
                )

                this.store.setKey('loading', false)
                break
            case ROUTES.external_assets.path:
            case ROUTES.external_assets.children.domains.path:
            case ROUTES.external_assets.children.technologies.path:
                const [
                    { AssetsModule },
                    { TechnologiesModule },
                    { IssuesModule },
                    { DomainsModule: DomainsModuleForDigitalFootprint },
                ] = await Promise.all([
                    import('@core/modules/digital-footprint/assets'),
                    import('@core/modules/digital-footprint/technologies/technologies.module'),
                    import('@core/modules/issues'),
                    import('@core/modules/domains/domains.module'),
                ])
                this.store.setKey('loading', false)
                await Promise.all([
                    TechnologiesModule.loadTechnologies(force),
                    AssetsModule.loadAssets(force),
                    IssuesModule.loadIssues(force),
                    DomainsModuleForDigitalFootprint.getAccountDomains(),
                ])
                TechnologiesModule.pollForTechnologies()
                break
            case ROUTES.fraud_defense.path:
            case ROUTES.fraud_defense.children.open_issues.path:
            case ROUTES.fraud_defense.children.configurations.path:
            case ROUTES.integrations.path: {
                const [
                    { AssetsModule: FraudDefenseAssetsModule },
                    { IntegrationsModule },
                    { IssuesModule: IntegrationIssuesModule },
                ] = await Promise.all([
                    await import('@core/modules/digital-footprint/assets'),
                    await import('@core/modules/integrations'),
                    await import('@core/modules/issues'),
                ])
                ;(IntegrationsService.getIntegrationsRequests(),
                    await Promise.all([
                        FraudDefenseAssetsModule.loadAssets(force),
                        IntegrationsModule.loadIntegrations(force),
                        IntegrationIssuesModule.loadIssues(),
                    ]))
                this.store.setKey('loading', false)
                break
            }
            case ROUTES.settings.path:
            case ROUTES.settings.children.account.path: {
                const [{ DomainsModule }] = await Promise.all([
                    await import('@core/modules/domains/domains.module'),
                ])
                this.store.setKey('loading', false)
                await DomainsModule.getAccountDomains()
                break
            }
            case ROUTES.settings.children.user.path: {
                const [{ UserModule }] = await Promise.all([
                    await import('@core/modules/user/user.module'),
                ])
                this.store.setKey('loading', false)
                await UserModule.onLoad()
                break
            }
            case ROUTES.settings.children.team.path: {
                const [{ SettingsTeamModule }] = await Promise.all([
                    await import('@core/modules/team/team.module'),
                ])
                this.store.setKey('loading', false)
                await SettingsTeamModule.loadTeamMembers(force)
                break
            }
            case ROUTES.users.path:
            case ROUTES.devices.path:
                const [
                    { IssuesModule: IssuesForDevicesModule },
                    { AssetsModule: AssetsForDevicesModule },
                    { IntegrationsModule: IntegrationsForDevicesModule },
                ] = await Promise.all([
                    import('@core/modules/issues'),
                    import('@core/modules/digital-footprint/assets'),
                    import('@core/modules/integrations'),
                ])
                this.store.setKey('loading', false)
                const forceAssets =
                    location.pathname === ROUTES.users.path || force
                await Promise.all([
                    AssetsForDevicesModule.loadAssets(forceAssets),
                    IntegrationsForDevicesModule.loadIntegrations(force),
                    IssuesForDevicesModule.loadIssues(force),
                ])
                break
            default:
                this.store.setKey('loading', false)
                break
        }

        Analytics.event('SEM::app_handleRouteDataLoad_end', {
            ts: new Date(),
            path: location.pathname,
        })
    }

    private async onScanInProgress() {
        this.log('AppModule on scan in progress')
        this.store.setKey('scanInProgress', true)
    }

    /**
     * @description reloads data with a delay of 3s on scan finished event
     * @private
     * Reloads:
     * - assets & domains
     * - technologies
     * - issues
     * - account domains
     * - security assessment
     */
    private async onScanFinished() {
        const [
            { AssetsModule },
            { TechnologiesModule },
            { IssuesModule },
            { SecurityAssessmentsModule },
        ] = await Promise.all([
            await import('@core/modules/digital-footprint/assets/assets.module'),
            await import('@core/modules/digital-footprint/technologies/technologies.module'),
            await import('@core/modules/issues/issues.module'),
            await import('@core/modules/security-assessments/security-assessments.module'),
        ])

        await Promise.all([
            await AssetsModule.loadAssets(true),
            await TechnologiesModule.loadTechnologies(true),
            await IssuesModule.loadIssues(true),
            await SecurityAssessmentsModule.loadSecurityAssessments(true),
            await DomainsModule.getAccountDomains(),
        ])

        Analytics.event('SEM::app_onScanFinished')

        this.store.setKey('scanInProgress', false)
        this.$eventBus.emit('security_configurations_scan_loading', {
            isLoading: false,
        })
    }

    private handleMfaEnforcement() {
        DialogService.showDialog(ASM_DIALOGS_MAP.ENABLE_MFA)
    }

    public async refresh() {
        const [
            { AssetsModule },
            { TechnologiesModule },
            { IssuesModule },
            { SecurityAssessmentsModule },
        ] = await Promise.all([
            await import('@core/modules/digital-footprint/assets/assets.module'),
            await import('@core/modules/digital-footprint/technologies/technologies.module'),
            await import('@core/modules/issues/issues.module'),
            await import('@core/modules/security-assessments/security-assessments.module'),
        ])

        await Promise.all([
            AssetsModule.loadAssets(true),
            TechnologiesModule.loadTechnologies(true),
            IssuesModule.loadIssues(true),
            SecurityAssessmentsModule.loadSecurityAssessments(true),
            UserInputsModule.pollProcesses(),
            DomainsModule.getAccountDomains(),
        ])
    }
}

export const AppModule = new App(state)
